import {
  always,
  evolve,
  filter,
  map,
  path,
  pathOr,
  pipe,
  prop,
  propOr,
  allPass,
  includes,
  both,
  ifElse,
  pathEq,
  cond,
  find,
  equals,
  T,
  __,
  values,
  memoizeWith,
  isNil
} from 'ramda'
import { utils } from '@digital-commerce/shared-utils'
import { buildAddonItemsText, buildHSCreditList } from '../../../utils/display'
import { formatCurrency } from '../../../utils/currency'
import {
  getItemWithTags,
  getRecurringPromotionsForNoTermService,
  getRecurringPromotionsWhereDurationIsLessThanItemRecurringDuration,
  getCacheKey
} from '../../../utils/cart'
import { getDiscountDisplayName } from '../../../utils/discount'

const {
  getHSService,
  getMonthlyItemPrice,
  getMonthlyDiscounts,
  getDisplayName,
  hasContractTerm,
  getMonthlyAddons
} = utils.cart

const BUSINESS_CONNECT_FEATURE_FLAG = 'businessConnect'

// Refactored from getBusinessConnectAddons & getAddonsOfService
export const getBConnectAddons = pipe(
  propOr([], 'items'),
  filter(pipe(prop('tags'), allPass([includes('business-connect'), includes('addon')])))
)

const getBusinessConnectInstallItem = (service) => {
  const items = pathOr([], ['items'])(service)
  const installItem = items.find((item) =>
    both(includes('business-connect'), includes('installation-fee'))(item.tags)
  )
  return {
    ...installItem,
    items
  }
}

const getSeatsItem = (service) => {
  const items = pathOr([], ['items'])(service)
  const seat = items.find((item) => item.tags.includes('business-connect-seat'))
  return {
    ...seat,
    items
  }
}

const getBusinessConnectInstallType = (service) => {
  const installItem = getBusinessConnectInstallItem(service)
  if (pathEq(['productOffering', 'name'], 'SBS - Bconn - Self Installation')(installItem))
    return 'SELF_INSTALL'

  if (pipe(pathOr([], ['tags']), includes('virtual-installation-fee'))(installItem))
    return 'VIRTUAL_INSTALL'

  return 'PRO_INSTALL'
}

const getPricePerSeat = (item, lang) => {
  const seat = getSeatsItem(item)

  const seatQuantity = pathOr(0, ['quantity'])(seat)

  if (seatQuantity === 0) return null

  const duration = path(['contractTerm', 'recurringDuration'])(item)

  const { amount: totalMonthlyPrice } = getMonthlyItemPrice(item)
  if (totalMonthlyPrice <= 0) return null

  const priceBySeat = totalMonthlyPrice / seatQuantity
  const formattedPriceBySeat = formatCurrency(priceBySeat, lang)

  return {
    price: formattedPriceBySeat,
    quantity: seatQuantity,
    duration
  }
}

// Exception function for no term services
const getRecurringPromotionsWithoutRecurringDurationForNoTermService = cond([
  [
    pathEq(['contractTerm', 'recurringDuration'], 0),
    pipe(
      pathOr([], ['itemPrice', 'recurring']),
      find(
        allPass([
          pathEq(['recurringPeriod', 'every'], 1),
          pathEq(['recurringPeriod', 'unit'], 'MONTH')
        ])
      ),
      propOr([], 'alterations'),
      filter(pipe(prop('recurringDuration'), equals(__, 0)))
    )
  ],
  [T, always([])]
])

const getBusinessConnectAdditionalHardware = pipe(
  getItemWithTags(['hardware']),
  filter((item) => getMonthlyItemPrice(item).amount > 0)
)

const getBusinessConnectAdditionalCharges = (cart) =>
  pipe(path(['items']), getItemWithTags(['business-connect', 'fee']), values)(cart)

export const getBusinessConnectService = memoizeWith(getCacheKey('Business-connect'), (cart) => {
  if (!cart?.root?.items) return undefined

  return pipe(
    getHSService(['business-connect']),
    evolve({
      items: map((id) => path(['items', id], cart))
    })
  )(cart)
})

export const isBusinessConnectFeatureEnabled = (featureFlags) => {
  return featureFlags?.[BUSINESS_CONNECT_FEATURE_FLAG] === true
}

const getNumberOfSeats = (item) => {
  const seat = getSeatsItem(item)

  const seatQuantity = pathOr(0, ['quantity'])(seat)
  const seatDisplayName = pathOr('', ['productOffering', 'displayName'])(seat)

  if (seatQuantity === 0) return null

  return {
    quantity: seatQuantity,
    displayName: seatDisplayName
  }
}

export const getAttributesToAppend = (businessConnectService, translate) => {
  const appendedAttributes = []

  // Number of seats
  const numberOfSeats = getNumberOfSeats(businessConnectService)
  if (numberOfSeats) {
    appendedAttributes.push(`${numberOfSeats.displayName}: ${numberOfSeats.quantity}`)
  }

  // Install type
  const businessConnectInstallType = getBusinessConnectInstallType(businessConnectService)
  const installTypeText =
    businessConnectInstallType === 'SELF_INSTALL'
      ? translate('item.selfInstall')
      : translate('item.proInstall')

  appendedAttributes.push(installTypeText)

  // Addon items
  const addons = getBConnectAddons(businessConnectService)
  if (addons?.length > 0) {
    const addonItemsText = buildAddonItemsText(addons, translate)

    appendedAttributes.push(addonItemsText)
  }

  return appendedAttributes
}

export const modifyItemPriceToUseSeat = (bConnectService) => {
  const seat = getSeatsItem(bConnectService)

  return {
    ...bConnectService,
    itemPrice: seat.itemPrice
  }
}

export const getItemPriceSeatsSubtitleText = (businessConnectWithSeatPrice, translate, lang) => {
  const seatsPrice = getPricePerSeat(businessConnectWithSeatPrice, lang)

  const seatsSubtitle = seatsPrice
    ? translate('seatsSubtitle', {
        replacements: {
          price: seatsPrice.price,
          quantity: seatsPrice.quantity
        }
      })
    : ''

  return seatsSubtitle
}

export const buildRecurringDiscountsList = (bConnectService, lang) => {
  const recurringDiscount = ifElse(
    hasContractTerm,
    getMonthlyDiscounts,
    getRecurringPromotionsWithoutRecurringDurationForNoTermService
  )

  const monthlyDiscounts = recurringDiscount(bConnectService)

  return monthlyDiscounts.map((discount, idx) => ({
    name: getDiscountDisplayName(discount),
    price: `-${formatCurrency(discount.amount, lang)}/mo.`,
    id: `${bConnectService.id}-discount-${idx}`,
    prefixIcon: discount.isCouponCode ? 'PIGGYBANK' : 'DEALS'
  }))
}

export const buildInstallItemMonthlyAmountData = (bConnectService, translate, lang) => {
  const installItem = getBusinessConnectInstallItem(bConnectService)
  // Break if there is no install item to prevent crash
  if (!installItem.id) return null

  const installType = getBusinessConnectInstallType(bConnectService)
  // Don't show self install or virtual install in price breakdown
  if (installType !== 'PRO_INSTALL') return null

  const pricePerSeat = getPricePerSeat(installItem, lang)
  const translateReplacements = {
    price: pricePerSeat?.price,
    duration: pricePerSeat?.duration,
    quantity: pricePerSeat?.quantity
  }
  const installItemSubtitle = pricePerSeat
    ? translate('proInstallSubtitle', { replacements: translateReplacements })
    : ''

  const name = `${getDisplayName(installItem)} ${installItemSubtitle}`
  const price = `${formatCurrency(getMonthlyItemPrice(installItem).amount, lang)}/mo.`
  return {
    name,
    price,
    id: installItem.id
  }
}

export const buildMonthlyAddons = (bConnectService, lang) => {
  const addons = getBConnectAddons(bConnectService)
  const monthlyAddons = getMonthlyAddons(addons)

  return monthlyAddons.map((addon) => ({
    id: addon.id,
    name: getDisplayName(addon),
    price: `${formatCurrency(getMonthlyItemPrice(addon).amount, lang)}/mo.`
  }))
}

export const buildAdditionalDevices = (bConnectService, lang) => {
  const items = pathOr([], ['items'])(bConnectService)
  const additionalDevices = getBusinessConnectAdditionalHardware(items)

  return additionalDevices.map((device) => ({
    id: device.id,
    name: `${device.quantity} ${getDisplayName(device)}`,
    price: `${formatCurrency(getMonthlyItemPrice(device).subTotal, lang)}/mo.`
  }))
}

export const buildAdditionalChargesList = (cart, lang) => {
  const additionalCharges = getBusinessConnectAdditionalCharges(cart)
  const getChargeName = (charge) =>
    charge.quantity > 1
      ? `${charge.quantity} ${getDisplayName(charge)}`
      : `${getDisplayName(charge)}`

  return additionalCharges.map((charge) => ({
    id: charge.id,
    name: getChargeName(charge),
    price: `${formatCurrency(getMonthlyItemPrice(charge).amount, lang)}`
  }))
}

const getBConnectOneTimeDiscountsList = (bConnectService, lang) => {
  let discounts
  if (hasContractTerm(bConnectService)) {
    discounts = getRecurringPromotionsWhereDurationIsLessThanItemRecurringDuration([
      bConnectService
    ])
  } else {
    discounts = getRecurringPromotionsForNoTermService(bConnectService)
  }
  return discounts.map((discount) => ({
    id: discount.id,
    name: getDiscountDisplayName(discount),
    price: `-${formatCurrency(discount.amount, lang)}`,
    prefixIcon: discount.isCouponCode ? 'PIGGYBANK' : 'DEALS'
  }))
}

export const buildAdditionalCreditsAndDiscounts = (bConnectService, cart, lang) => {
  const discounts = getBConnectOneTimeDiscountsList(bConnectService, lang)
  const credits = buildHSCreditList('business-connect', cart, lang)

  return [...discounts, ...credits]
}

export const shouldRender = (cart, featureFlags) => {
  const businessConnect = getBusinessConnectService(cart)
  return !isNil(businessConnect) && isBusinessConnectFeatureEnabled(featureFlags)
}
